<template>
  <Transition name="cookie-banner">
    <div v-if="showBanner" class="cookie-banner-overlay">
      <div class="cookie-banner">
        <div class="cookie-banner-top-accent" />

        <!-- Simple Banner View -->
        <div v-if="!showSettings" class="cookie-banner-simple">
          <div class="cookie-banner-text">
            <h3 class="cookie-banner-title">{{ $t('cookies.banner_title') }}</h3>
            <p class="cookie-banner-description">{{ $t('cookies.banner_description') }}</p>
          </div>
          <div class="cookie-banner-actions">
            <button class="cookie-btn cookie-btn-primary" @click="acceptAll">
              {{ $t('cookies.accept_all') }}
            </button>
            <button class="cookie-btn cookie-btn-ghost" @click="showSettings = true">
              {{ $t('cookies.manage') }}
            </button>
          </div>
        </div>

        <!-- Settings Panel View -->
        <div v-else class="cookie-banner-settings">
          <h3 class="cookie-banner-title">{{ $t('cookies.manage') }}</h3>

          <div class="cookie-categories">
            <div class="cookie-category">
              <div class="cookie-category-header">
                <div class="cookie-category-info">
                  <span class="cookie-category-name">{{ $t('cookies.necessary_title') }}</span>
                  <span class="cookie-category-desc">{{ $t('cookies.necessary_description') }}</span>
                </div>
                <label class="cookie-toggle">
                  <input type="checkbox" checked disabled />
                  <span class="cookie-toggle-slider" />
                </label>
              </div>
            </div>

            <div class="cookie-category">
              <div class="cookie-category-header">
                <div class="cookie-category-info">
                  <span class="cookie-category-name">{{ $t('cookies.functional_title') }}</span>
                  <span class="cookie-category-desc">{{ $t('cookies.functional_description') }}</span>
                </div>
                <label class="cookie-toggle">
                  <input v-model="preferences.functional" type="checkbox" />
                  <span class="cookie-toggle-slider" />
                </label>
              </div>
            </div>

            <div class="cookie-category">
              <div class="cookie-category-header">
                <div class="cookie-category-info">
                  <span class="cookie-category-name">{{ $t('cookies.analytics_title') }}</span>
                  <span class="cookie-category-desc">{{ $t('cookies.analytics_description') }}</span>
                </div>
                <label class="cookie-toggle">
                  <input v-model="preferences.analytics" type="checkbox" />
                  <span class="cookie-toggle-slider" />
                </label>
              </div>
            </div>

            <div class="cookie-category">
              <div class="cookie-category-header">
                <div class="cookie-category-info">
                  <span class="cookie-category-name">{{ $t('cookies.marketing_title') }}</span>
                  <span class="cookie-category-desc">{{ $t('cookies.marketing_description') }}</span>
                </div>
                <label class="cookie-toggle">
                  <input v-model="preferences.marketing" type="checkbox" />
                  <span class="cookie-toggle-slider" />
                </label>
              </div>
            </div>
          </div>

          <div class="cookie-banner-actions">
            <button class="cookie-btn cookie-btn-primary" @click="handleSave">
              {{ $t('cookies.save') }}
            </button>
            <button class="cookie-btn cookie-btn-ghost" @click="handleAcceptAll">
              {{ $t('cookies.accept_all') }}
            </button>
            <button class="cookie-btn cookie-btn-ghost" @click="handleDeclineAll">
              {{ $t('cookies.decline_all') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
const { state, showBanner, acceptAll, declineAll, savePreferences, hasConsented } = useCookieConsent()

const showSettings = ref(false)

const preferences = reactive({
  functional: state.value.functional,
  analytics: state.value.analytics,
  marketing: state.value.marketing,
})

// When banner re-opens (e.g. from footer link), sync preferences with current state
watch(showBanner, (visible) => {
  if (visible) {
    preferences.functional = state.value.functional
    preferences.analytics = state.value.analytics
    preferences.marketing = state.value.marketing
    // If user already consented and re-opens, go straight to settings
    showSettings.value = hasConsented.value
  } else {
    showSettings.value = false
  }
})

function handleSave() {
  savePreferences(preferences)
}

function handleAcceptAll() {
  acceptAll()
}

function handleDeclineAll() {
  declineAll()
}
</script>

<style scoped>
.cookie-banner-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  /* Must stay above ShopMasterPlayer (z-9999) so the Accept button is
     never obscured by the floating audio bar on first-visit shop pages. */
  z-index: 10001;
  padding: var(--space-4);
  pointer-events: none;
}

.cookie-banner {
  max-width: 1200px;
  margin: 0 auto;
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  pointer-events: auto;
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.5);
}

.cookie-banner-top-accent {
  height: 2px;
  background: linear-gradient(90deg, var(--primary), transparent);
}

.cookie-banner-simple {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-6);
  padding: var(--space-6);
}

.cookie-banner-text {
  flex: 1;
}

.cookie-banner-title {
  font-family: var(--font-heading);
  font-size: 1.1rem;
  color: var(--text-main);
  margin: 0 0 var(--space-2) 0;
}

.cookie-banner-description {
  color: var(--text-muted);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
}

.cookie-banner-actions {
  display: flex;
  gap: var(--space-3);
  flex-shrink: 0;
}

.cookie-btn {
  min-height: 44px; /* touch-target minimum */
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: none;
}

.cookie-btn-primary {
  background: var(--primary);
  color: #030304;
  font-weight: 600;
}

.cookie-btn-primary:hover {
  box-shadow: var(--glow-primary);
  transform: translateY(-1px);
}

.cookie-btn-ghost {
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border-active);
}

.cookie-btn-ghost:hover {
  color: var(--text-main);
  border-color: var(--primary);
}

/* Settings Panel */
.cookie-banner-settings {
  padding: var(--space-6);
}

.cookie-categories {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: var(--space-4) 0 var(--space-6);
}

.cookie-category {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: var(--space-4);
}

.cookie-category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.cookie-category-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.cookie-category-name {
  font-weight: 600;
  color: var(--text-main);
  font-size: 0.95rem;
}

.cookie-category-desc {
  color: var(--text-muted);
  font-size: 0.85rem;
  line-height: 1.4;
}

/* Toggle Switch */
.cookie-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.cookie-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cookie-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: var(--border-active);
  border-radius: var(--radius-full);
  transition: all 0.3s ease;
}

.cookie-toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: var(--text-main);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.cookie-toggle input:checked + .cookie-toggle-slider {
  background: var(--primary);
}

.cookie-toggle input:checked + .cookie-toggle-slider::before {
  transform: translateX(20px);
}

.cookie-toggle input:disabled + .cookie-toggle-slider {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Transition */
.cookie-banner-enter-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
}

.cookie-banner-leave-active {
  transition: transform 0.3s ease, opacity 0.2s ease;
}

.cookie-banner-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.cookie-banner-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Mobile */
@media (max-width: 768px) {
  .cookie-banner-overlay {
    padding: var(--space-2);
  }

  .cookie-banner-simple {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-4);
    padding: var(--space-4);
  }

  .cookie-banner-actions {
    flex-direction: column;
  }

  .cookie-btn {
    text-align: center;
    padding: var(--space-3) var(--space-4);
  }

  .cookie-banner-settings {
    padding: var(--space-4);
  }
}
</style>
